import { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import type { AdEntityListGroupDto } from '@terra/shared-contracts-internal-network';
import { useListPageGroupCollapse } from '../../../common/useListPageGroupCollapse';
import {
  INTERNAL_ASSETS_EMPTY_GROUP_KEY,
  buildInternalAssetsTableRows,
  isInternalAssetsGroupHeaderRow,
  resolveInternalAssetsLoadMoreRowIndex,
  type InternalAssetGridItem,
  type InternalAssetsGroupByField,
} from './internal-assets-table.model';

export function useInternalAssetsTableRows({
  items,
  groupBy,
  groups,
  resetKey,
}: {
  items: readonly InternalAssetGridItem[];
  groupBy: InternalAssetsGroupByField | undefined;
  groups: readonly AdEntityListGroupDto[];
  resetKey: string;
}) {
  const tInternalNetwork = useTranslations('internalNetwork');
  const tAssetList = useTranslations('internalNetwork.assetList');
  const tTable = useTranslations('proxy.assets.table');
  const tCommon = useTranslations('common');
  const {
    collapsedGroupKeys,
    toggleGroup,
    expandAllGroups,
    collapseGroups,
    areAllCollapsed,
  } = useListPageGroupCollapse({ resetKey });

  const getGroupLabel = useCallback(
    (groupKey: string): string => {
      if (groupKey === INTERNAL_ASSETS_EMPTY_GROUP_KEY) {
        return tTable('emptyGroup');
      }

      switch (groupBy) {
        case 'entityKind':
          return tInternalNetwork(
            `crownJewels.entityKinds.${groupKey}` as Parameters<
              typeof tInternalNetwork
            >[0],
          );
        case 'assetKind':
          return tAssetList(
            `assetKind.${groupKey}` as Parameters<typeof tAssetList>[0],
          );
        case 'isCrownJewel':
          return groupKey === 'true'
            ? tInternalNetwork('crownJewels.title')
            : tCommon('other');
        case 'isOwned':
          return groupKey === 'true'
            ? tInternalNetwork('ownedPrincipals.title')
            : tCommon('other');
        case undefined:
          return groupKey;
      }
    },
    [groupBy, tAssetList, tCommon, tInternalNetwork, tTable],
  );

  const rows = useMemo(
    () =>
      buildInternalAssetsTableRows({
        items,
        groupBy,
        groups,
        collapsedGroupKeys,
        getGroupLabel,
      }),
    [collapsedGroupKeys, getGroupLabel, groupBy, groups, items],
  );
  const loadMoreRowIndex = resolveInternalAssetsLoadMoreRowIndex({
    rows,
    groupBy,
    collapsedGroupKeys,
  });
  const groupKeys = useMemo(
    () =>
      rows.flatMap((row) =>
        isInternalAssetsGroupHeaderRow(row) ? [row.groupKey] : [],
      ),
    [rows],
  );

  return {
    rows,
    loadMoreRowIndex,
    collapsedGroupKeys,
    areAllGroupsCollapsed: areAllCollapsed(groupKeys),
    toggleGroup,
    expandAllGroups,
    collapseAllGroups: () => collapseGroups(groupKeys),
  };
}
